import { CustomErrorType } from './HTTPErrors';

/**
 * Type definition for error responses from API
 */
export type ErrorResponse = {
  error: CustomErrorType;
};

/**
 * Safely checks if a value is an error response.
 * Handles null, undefined, and non-object values gracefully.
 *
 * @param value - The value to check (can be null, undefined, or any type)
 * @returns true if value is an object with an 'error' property, false otherwise
 *
 * @example
 * ```ts
 * const data = await fetchData();
 * if (hasErrorResponse(data)) {
 *   // Handle error
 *   return;
 * }
 * // Safe to use data as success response
 * ```
 */
export function hasErrorResponse(value: unknown): value is ErrorResponse {
  return (
    value !== null &&
    value !== undefined &&
    typeof value === 'object' &&
    'error' in value
  );
}

/**
 * Safely checks if a value is NOT an error response (i.e., a success response).
 * Handles null, undefined, and non-object values gracefully.
 *
 * @param value - The value to check (can be null, undefined, or any type)
 * @returns true if value is not null/undefined and is not an error response
 *
 * @example
 * ```ts
 * const data = await fetchData();
 * if (isSuccessResponse(data)) {
 *   // Safe to use data
 * }
 * ```
 */
export function isSuccessResponse<T>(
  value: unknown,
): value is Exclude<T, ErrorResponse> {
  if (value === null || value === undefined) {
    return false;
  }

  // Primitive types are considered success responses
  if (
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  ) {
    return true;
  }

  // For objects, check that it's not an error response
  return typeof value === 'object' && !('error' in value);
}

/**
 * Type guard that checks if a value is an error response.
 * This is a type-safe version that works with union types.
 *
 * @param value - The value to check
 * @returns Type predicate indicating if value is ErrorResponse
 *
 * @example
 * ```ts
 * type ApiResponse = ErrorResponse | SuccessResponse<Data>;
 * const response: ApiResponse = await fetchData();
 *
 * if (isErrorResponse(response)) {
 *   // TypeScript knows response is ErrorResponse here
 *   console.error(response.error);
 * } else {
 *   // TypeScript knows response is SuccessResponse<Data> here
 *   console.log(response);
 * }
 * ```
 */
export function isErrorResponse<T>(
  value: ErrorResponse | T | null | undefined,
): value is ErrorResponse {
  return hasErrorResponse(value);
}
